import { bootstrapPreview } from '@/auth/rolePreview';
import {
  type BootstrapOutcome,
  type Chooser,
  bootstrapAssignment,
  requestRoleChoice,
} from '@/auth/roleSelection';
import { ApiProvider } from '@/provider/ApiProvider';

/**
 * The role part of a page load (Authorization-Plan §6.2 step 6, §13.6; UI
 * state plan phase 1), once per page load:
 *
 * 1. the active role assignment is confirmed with the identity service and
 *    becomes this tab's `X-PNET-Expected-Assignment`;
 * 2. the role's session data (permissions map, landing modules, master data)
 *    and its last header selection (brand, partner, filter; step A) are
 *    loaded into memory: neither is in browser storage, so a hard refresh
 *    starts without them;
 * 3. a running role preview (§13.7) is read: banner + the previewed context type.
 *
 * It runs in two places that share one result:
 * - `preloadRole()`, before the app renders (auth/sessionBoot.ts): everything
 *   that needs no question. The app's providers then mount with the same
 *   session they found in browser storage before;
 * - `runRoleBootstrap()`, in RoleGate: the same, and when the user has several
 *   roles and none is active it shows the selection screen first.
 */
let bootstrap: Promise<BootstrapOutcome> | null = null;

const loadRole = async (outcome: BootstrapOutcome): Promise<BootstrapOutcome> => {
  if (outcome === 'ready') {
    await Promise.all([
      // nothing is loaded again after a sign-in or role switch on this page
      ApiProvider.loadRoleSession().catch(() => undefined),
      // the role's last header selection (brand, partner, filter): kept on the server
      ApiProvider.loadHeaderSelection().catch(() => undefined),
      // a running role preview (§13.7): banner + the previewed context type
      bootstrapPreview(),
    ]);
  }
  return outcome;
};

/** Once per page load: the active assignment (asking when needed), its data, a preview. */
export const runRoleBootstrap = (
  choose: Chooser = requestRoleChoice,
): Promise<BootstrapOutcome> => {
  bootstrap ??= bootstrapAssignment(choose)
    .then(loadRole)
    .catch(() => 'ready' as const);
  return bootstrap;
};

/** The user has several roles and none is active: only the selection screen can go on. */
export class RoleChoiceNeeded extends Error {
  constructor() {
    super('role_choice_needed');
    this.name = 'RoleChoiceNeeded';
  }
}

const deferChoice: Chooser = () => Promise.reject(new RoleChoiceNeeded());

/**
 * Before the app renders: the role bootstrap without a question. When a role
 * has to be chosen nothing is kept, and RoleGate asks (the selection screen
 * needs the rendered app). null = a role has to be chosen.
 */
export async function preloadRole(): Promise<BootstrapOutcome | null> {
  if (bootstrap) return bootstrap;
  const attempt = bootstrapAssignment(deferChoice)
    .then(loadRole)
    .catch((error): BootstrapOutcome => {
      if (error instanceof RoleChoiceNeeded) throw error;
      return 'ready';
    });
  bootstrap = attempt;
  try {
    return await attempt;
  } catch {
    if (bootstrap === attempt) bootstrap = null;
    return null;
  }
}

/**
 * Forget this page load's role bootstrap, so the next call runs it again: the
 * retry after a page load whose role data did not arrive (auth/sessionBoot.ts).
 * The confirmed assignment stays; only what is missing is loaded again.
 */
export const forgetRoleBootstrap = (): void => {
  bootstrap = null;
};

/** Test helper. */
export const resetRoleBootForTests = forgetRoleBootstrap;
